import * as React from 'react'
import { Tree, Empty, Spin, Tooltip } from 'antd'
import type { DataNode } from 'antd/es/tree'
import { useNavigate, useLocation } from 'react-router-dom'
import { getRet00cOverviewTree } from '../../common/services/ret00cs'

const HTTP_COLOR: Record<string, string> = {
  GET: '#52c41a',
  POST: '#1890ff',
  PUT: '#faad14',
  DELETE: '#ff4d4f',
  PATCH: '#722ed1',
}

function buildTreeNodes(items: any[]): DataNode[] {
  return items.map((item) => {
    const isGroup = item.node_type === 'group'
    const children = item.children && item.children.length > 0 ? buildTreeNodes(item.children) : undefined
    const label = isGroup ? (
      <Tooltip title={item.title} placement="right">
        <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📁 {item.title}
        </span>
      </Tooltip>
    ) : (
      <Tooltip title={`${item.http_method || 'GET'} ${item.path}`} placement="right">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
          <span
            style={{
              color: HTTP_COLOR[(item.http_method || 'GET').toUpperCase()] || '#333',
              fontSize: 11,
              fontWeight: 700,
              minWidth: 38,
              flexShrink: 0,
            }}
          >
            {(item.http_method || 'GET').toUpperCase()}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.title}
          </span>
        </span>
      </Tooltip>
    )
    return {
      key: item.key,
      title: label,
      isLeaf: item.isLeaf === true || isGroup === false && !children,
      children,
      dataRef: item,
    } as DataNode
  })
}

export default function Ret00cSideMenu(): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const [treeData, setTreeData] = React.useState<DataNode[]>([])
  const [loading, setLoading] = React.useState(true)

  const fetchTree = React.useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await getRet00cOverviewTree()
      const items = res?.data?.items || []
      setTreeData(buildTreeNodes(items))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchTree()
  }, [fetchTree])

  const selectedKeys = React.useMemo(() => {
    const hash = location.hash.replace(/^#/, '')
    const m = hash.match(/\/ret00cs\/(\d+)/)
    if (m) return [`ret00c-${m[1]}`]
    return []
  }, [location])

  const handleSelect = (_selectedKeys: React.Key[], info: any) => {
    const node = info.node as any
    const ref: any = node.dataRef
    if (!ref) return
    if (ref.node_type === 'interface' || ref.node_type !== 'group') {
      navigate(`/ret00cs/${ref.id}`)
    }
  }

  return (
    <div style={{ height: '100%', padding: 12, overflow: 'auto' }}>
      <div style={{ padding: '4px 8px 12px', fontWeight: 600, color: '#1f1f1f' }}>
        接口列表
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
      ) : treeData.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
      ) : (
        <Tree
          blockNode
          showLine={{ showLeafIcon: false }}
          defaultExpandAll
          selectedKeys={selectedKeys as any}
          onSelect={handleSelect}
          treeData={treeData}
          style={{ fontSize: 13 }}
        />
      )}
      <style>{`
        .ant-tree { font-size: 13px; }
        .ant-tree-node-content-wrapper { overflow: hidden; }
        .ant-tree-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ant-tree-show-line li { padding: 0; }
      `}</style>
    </div>
  )
}
